import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/foam-roof-recoating-cost-in-phoenix";
const TITLE = "Foam Roof Recoating Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "Real Phoenix foam roof recoating costs: per-square-foot pricing, what drives the price, and how to make your coating last. From a family-owned roofer since 1989.";

const faqs = [
  { q: "How much does foam roof recoating cost per square foot in Phoenix?", a: "Typical Phoenix recoats run $1.00–$3.00 per square foot installed, including pressure cleaning, primer, and two coats of elastomeric. Silicone tends to land at the high end; acrylic at the low end." },
  { q: "How often should a foam roof be recoated?", a: "Every 5 years in the Phoenix Valley. Our 11+ UV index ages coatings far faster than the national average — south- and west-facing slopes wear first." },
  { q: "Is recoating worth it vs. replacing the foam?", a: "Almost always, if you recoat on time. A typical Phoenix recoat protects a roof that costs $7,500–$12,000+ to replace. The recoat is the cheapest roof you'll ever buy — and it renews your Durafoam install warranty by another 5 years." },
  { q: "Does the price include repairs?", a: "Light prep — bird pecks, small voids — is included. Larger repairs (water-damaged foam, ponding areas) are quoted separately so you see what you're paying for." },
];

export const Route = createFileRoute("/foam-roof-recoating-cost-in-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Foam Roof Recoating",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="foam-roof-recoating-cost-in-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Foam Roof Recoating Cost in Phoenix"
      subhead="Honest 2026 price ranges for elastomeric recoats on Phoenix-area foam roofs — with the factors that move the number up or down."
      heroBullets={[
        "Typical Phoenix range: $1.00–$3.00 per sq ft",
        "Most 2,000 sq ft Phoenix roofs: $2,000–$4,000",
        "Includes pressure clean, primer & two coats",
        "5-year workmanship warranty on every recoat",
      ]}
      intro={
        <>
          <p>
            The typical cost of a foam roof recoat in Phoenix is <strong>$1.00–$3.00 per square foot</strong>,
            which puts most single-family Phoenix homes in the <strong>$2,000–$4,000</strong> range for a
            2,000 sq ft roof. That price reflects a properly cleaned and primed surface plus two
            coats of elastomeric at documented mil thickness — not a one-coat spray job.
          </p>
          <p>
            Recoating on schedule is the single biggest decision foam roof owners make. Done every
            5 years in Phoenix, it resets the roof's clock for another full coating cycle — and if
            your roof is still inside a Durafoam install warranty, the recoat renews that warranty
            for another 5 years. Skipped, the Phoenix sun starts breaking down the foam underneath
            and you're suddenly paying 3–5× more to fix it.
          </p>
        </>
      }
      pricingRows={[
        { label: "Small home (≤1,500 sq ft roof)", range: "$1,500–$3,200", notes: "Typical Phoenix pricing, acrylic or silicone" },
        { label: "Typical home (2,000 sq ft roof)", range: "$2,000–$4,000", notes: "Two-coat elastomeric system" },
        { label: "Larger home (2,500–3,000 sq ft)", range: "$2,800–$5,800", notes: "Includes minor prep" },
        { label: "HOA / multi-unit (per sq ft)", range: "$1.00–$2.20", notes: "Phoenix volume pricing" },
        { label: "Premium silicone upgrade", range: "+$0.40–$0.80 / sq ft", notes: "Longer service life, better ponding tolerance" },
      ]}
      pricingFootnote="Ranges reflect typical 2026 Phoenix Valley pricing for residential and HOA roofs in serviceable condition."
      factors={[
        { title: "Roof size & access", body: "Bigger roofs cost less per square foot. Tight access, multiple levels, or HVAC obstructions push the per-foot price up." },
        { title: "Coating system", body: "Acrylic is the entry option. Silicone costs more but handles ponding and UV better. We spec the system to your roof, not a one-size price." },
        { title: "Mil thickness", body: "Manufacturers require minimum dry-mil thickness for warranty. Cheaper bids often hit that by skimping on coats — we document wet-mil readings." },
        { title: "Prep & repair needs", body: "Heavy chalking, foam damage, or ponding areas add cost. We photograph everything so you see what you're paying for." },
      ]}
      saveMoney={{
        heading: "How to keep the recoat affordable",
        body: (
          <>
            <ul>
              <li><strong>Don't wait past year 5–6.</strong> In Phoenix, coatings are spent by year 5. Push past that and UV reaches the foam, turning a recoat into a repair-and-recoat — sometimes 2× the cost.</li>
              <li><strong>Skip the granulated upgrade if you don't walk the roof.</strong> Granules add cost and only matter for walkable areas.</li>
              <li><strong>Bundle with neighbors.</strong> HOAs and adjacent Phoenix homes that recoat together regularly save 15–25% per home.</li>
              <li><strong>Schedule outside monsoon.</strong> October–May is ideal. Summer recoats are doable but get rescheduled around storms.</li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Get an exact recoat number for your roof"
      ctaBody="Use the instant calculator for a 60-second estimate, or schedule a free on-site inspection and we'll measure, photograph, and write you a real quote."
    />
  );
}
